import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Phone, Mail, MapPin, Briefcase } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { MemberQuickNav } from '../MemberQuickNav';
import api from '../../lib/api';

const Row = ({ icon: Icon, children }) => (
  <p className="flex items-start gap-2 text-sm text-[#3c4a2a]/75">
    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#a3688f]" />
    <span className="break-words min-w-0">{children}</span>
  </p>
);

export default function MemberDirectoryPage() {
  const [members, setMembers] = useState(null);
  const backendBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get('/members/directory').then((res) => setMembers(res.data)).catch(() => setMembers([]));
  }, []);

  return (
    <main className="min-h-screen bg-[#f1e3eb] pt-44 pb-20 px-4" data-testid="member-directory-page">
      <div className="max-w-5xl mx-auto">
        <MemberQuickNav />
        <div className="text-center mb-10">
          <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">Members Only</span>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-2">Member Directory</h1>
          <p className="text-[#3c4a2a]/70 mt-3 max-w-xl mx-auto">
            Only members who have chosen to appear are listed, showing just the details they authorized.
          </p>
        </div>

        {members === null ? (
          <p className="text-center text-[#3c4a2a]/50">Loading…</p>
        ) : members.length === 0 ? (
          <div className="text-center bg-white rounded-xl shadow-sm p-10 max-w-lg mx-auto" data-testid="directory-empty">
            <Users className="w-10 h-10 text-[#bd8cae] mx-auto mb-3" />
            <p className="text-[#3c4a2a]/70 mb-5">
              No members have joined the directory yet. Opt in on your Member Information Form to be the first!
            </p>
            <Link to="/member-info">
              <Button data-testid="directory-fill-form-btn" className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
                Fill Out My Info Form
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((m) => (
              <div key={m.id} data-testid={`directory-card-${m.id}`} className="bg-white rounded-xl shadow-md border border-[#bd8cae]/20 p-6 text-center">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-[#bd8cae]/20 mx-auto mb-4 flex items-center justify-center">
                  {m.photo_url ? (
                    <img src={`${backendBase}${m.photo_url}`} alt={m.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-9 h-9 text-[#a3688f]" />
                  )}
                </div>
                <h3 className="font-serif text-xl text-[#3c4a2a]">{m.display_name}</h3>
                {m.title && (
                  <p className="text-sm text-[#a3688f] font-medium" data-testid={`directory-title-${m.id}`}>{m.title}</p>
                )}
                {m.full_name && m.preferred_name && m.full_name !== m.display_name && (
                  <p className="text-xs text-[#3c4a2a]/50">{m.full_name}</p>
                )}
                <Badge className="bg-[#5d6b3c]/10 text-[#5d6b3c] mt-2">{m.membership_level || 'Member'}</Badge>
                {m.founding_number ? (
                  <Badge className="bg-[#bd8cae]/25 text-[#a3688f] mt-2 ml-1">Founding Member #{m.founding_number}</Badge>
                ) : null}
                <div className="mt-4 space-y-2 text-left">
                  {m.profession && <Row icon={Briefcase}>{m.profession}</Row>}
                  {m.phone && <Row icon={Phone}><a href={`tel:${m.phone}`} className="hover:text-[#5d6b3c]">{m.phone}</a></Row>}
                  {m.email && <Row icon={Mail}><a href={`mailto:${m.email}`} className="hover:text-[#5d6b3c]">{m.email}</a></Row>}
                  {m.mailing_address && <Row icon={MapPin}>{m.mailing_address}</Row>}
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-sm text-[#3c4a2a]/60 mt-10">
          Want to appear here or change what's shown?{' '}
          <Link to="/member-info" className="text-[#a3688f] underline underline-offset-2" data-testid="directory-update-link">
            Update your Member Information Form
          </Link>
        </p>
      </div>
    </main>
  );
}
